import type { z } from 'zod';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBLessonSchema, LessonSchema } from './schemas';

type DBLesson = z.infer<typeof DBLessonSchema>;
type Lesson = z.infer<typeof LessonSchema>;

export const LessonCodecConfig: TransformerConfig<typeof LessonSchema, typeof DBLessonSchema> = {
  name: 'Lesson',
  clientSchema: LessonSchema,
  dbSchema: DBLessonSchema,
  transformers: [
    // classId: { subject, classId } ↔ subjectClassIdentifier: 'subject#classId'
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['classId'] as const,
        encode: ({ classId }) => ({
          subjectClassIdentifier: `${(classId as Lesson['classId']).subject}#${(classId as Lesson['classId']).classId}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectClassIdentifier'] as const,
        decode: ({ subjectClassIdentifier }) => {
          const parts = (subjectClassIdentifier as DBLesson['subjectClassIdentifier']).split('#');
          return {
            classId: {
              subject: parts[0] as Subject,
              classId: parts[1] as string,
            },
          };
        },
      },
    }),

    // lesson: { course, lessonId, counter } ↔ lesson: 'course#lessonId#counter'
    // Also generates course and courseLesson fields.
    // `course` is claimed as a source field so it does not leak through as
    // passthrough into the DB output (DBLessonSchema has no `course` field —
    // course is embedded in the composite keys). On decode, `course` is
    // reconstructed by splitting the composite `lesson` key.
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['lesson', 'course'] as const,
        encode: ({ lesson }) => {
          if (!lesson) return {};
          const l = lesson as Lesson['lesson'];
          return {
            lesson: `${l.course}#${l.lessonId}#${l.counter}`,
            courseLesson: `${l.course}#${l.lessonId}`,
          };
        },
      },
      decoder: {
        sourceFields: ['lesson', 'courseLesson'] as const,
        decode: ({ lesson }) => ({
          course: (lesson as DBLesson['lesson']).split('#')[0]!,
          lesson: {
            course: (lesson as DBLesson['lesson']).split('#')[0]!,
            lessonId: (lesson as DBLesson['lesson']).split('#')[1]!,
            counter: (lesson as DBLesson['lesson']).split('#')[2]!,
          },
        }),
      },
    }),

    // enrolledStudents: [{ studentId, name }] ↔ enrolledStudents: { studentId: { name } }
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['enrolledStudents'] as const,
        encode: ({ enrolledStudents }) => ({
          enrolledStudents: Object.fromEntries(
            (enrolledStudents as Lesson['enrolledStudents']).map(val => [
              val.studentId,
              { name: val.name },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['enrolledStudents'] as const,
        decode: ({ enrolledStudents }) => ({
          enrolledStudents: Object.entries(
            (enrolledStudents ?? {}) as NonNullable<DBLesson['enrolledStudents']>
          ).map(([studentId, { name }]) => ({
            studentId,
            name,
          })),
        }),
      },
    }),

    // rollMarked: boolean ↔ rollMarked: 'true' | 'false'
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['rollMarked'] as const,
        encode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as Lesson['rollMarked']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['rollMarked'] as const,
        decode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as DBLesson['rollMarked']) === 'true',
        }),
      },
    }),
  ],
};

export const encodeLessonToDb = createEncodeToDbFn(LessonCodecConfig);
export const decodeLessonFromDb = createDecodeFromDbFn(LessonCodecConfig);
